import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { vendorsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Loader2, Search, Store, Phone, Mail, Package, ChevronRight } from 'lucide-react';

const CATEGORIES = ['general', 'materials', 'furniture', 'fixtures', 'electrical', 'plumbing', 'painting', 'flooring', 'hvac', 'landscaping', 'other'];

export default function Vendors() {
  const { isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const canEdit = isAdmin || isStaff;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const emptyForm = { name: '', contact_person: '', email: '', phone: '', address: '', category: 'general', notes: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    vendorsAPI.list()
      .then(res => { setVendors(res.data); setLoading(false); })
      .catch((err) => { console.error('Failed to fetch vendors:', err.message); setLoading(false); });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editing) {
        const res = await vendorsAPI.update(editing.vendor_id, form);
        setVendors(vendors.map(v => v.vendor_id === editing.vendor_id ? res.data : v));
      } else {
        const res = await vendorsAPI.create(form);
        setVendors([res.data, ...vendors]);
      }
      setDialogOpen(false); setEditing(null); setForm(emptyForm);
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await vendorsAPI.delete(id); setVendors(vendors.filter(v => v.vendor_id !== id)); } catch (err) { console.error(err); }
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({ name: v.name, contact_person: v.contact_person || '', email: v.email || '', phone: v.phone || '', address: v.address || '', category: v.category || 'general', notes: v.notes || '' });
    setDialogOpen(true);
  };

  const filtered = vendors.filter(v => {
    const matchSearch = v.name?.toLowerCase().includes(searchTerm.toLowerCase()) || v.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = catFilter === 'all' || v.category === catFilter;
    return matchSearch && matchCat;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendors-page">
        <div className="flex items-center justify-between">
          <div><h1 className="page-title">Vendor Directory</h1><p className="text-sm text-muted-foreground">Manage suppliers, articles, and purchase orders</p></div>
          {canEdit && (<Button onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="add-vendor-btn"><Plus className="h-4 w-4 mr-2" />Add Vendor</Button>)}
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search vendors..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="vendor-search" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (<div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted rounded" />)}</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><Store className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No vendors found</p></CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Category</TableHead><TableHead className="text-center">Articles</TableHead><TableHead>Phone</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(v => (
                    <TableRow key={v.vendor_id} data-testid={`vendor-${v.vendor_id}`}>
                      <TableCell>
                        <button className="font-medium text-left hover:text-primary transition-colors flex items-center gap-1 group" onClick={() => navigate(`/vendors/${v.vendor_id}`)}>
                          {v.name}<ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </TableCell>
                      <TableCell className="text-sm">{v.contact_person || '—'}</TableCell>
                      <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded-full bg-muted">{v.category}</span></TableCell>
                      <TableCell className="text-center"><span className="flex items-center justify-center gap-1 text-sm text-muted-foreground"><Package className="h-3.5 w-3.5" />{v.article_count || 0}</span></TableCell>
                      <TableCell className="text-sm">{v.phone ? <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{v.phone}</span> : '—'}</TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(v)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(v.vendor_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Vendor Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="vendor-name-input" /></div>
                  <div className="space-y-2"><Label>Contact Person</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
                </div>
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="save-vendor-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? 'Update' : 'Add Vendor'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
